/**
 * Erreurs d’API côté client (DECISIONS §4.3 et §9.4).
 *
 * Règle : le message français renvoyé par le serveur n’est **jamais** affiché au joueur. Seul
 * `error.code` sert à choisir une clé `ui:errors.<CODE>` du catalogue d’interface. Ce module
 * est le seul endroit qui connaît cette correspondance.
 */
import { ERROR_CODES, type ErrorCode } from '@elemora/shared/api';

/** Vrai si la valeur est un code d’erreur du contrat (liste fermée §4.3). */
export function estCodeErreur(valeur: unknown): valeur is ErrorCode {
  return typeof valeur === 'string' && (ERROR_CODES as readonly string[]).includes(valeur);
}

/** Paramètres de construction d’une `ErreurApi`. */
export interface ParametresErreurApi {
  readonly code: ErrorCode;
  /** Message technique (logs, tests) : jamais rendu dans l’interface. */
  readonly message: string;
  readonly statutHttp: number;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  /** `true` si l’appel n’a jamais atteint le serveur (réseau coupé, API arrêtée). */
  readonly reseau?: boolean;
}

/** Erreur normalisée : tout échec d’appel devient une `ErreurApi`, jamais une exception brute. */
export class ErreurApi extends Error {
  readonly code: ErrorCode;
  readonly statutHttp: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;
  readonly reseau: boolean;

  constructor(parametres: ParametresErreurApi) {
    super(parametres.message);
    this.name = 'ErreurApi';
    this.code = parametres.code;
    this.statutHttp = parametres.statutHttp;
    this.details = parametres.details;
    this.requestId = parametres.requestId;
    this.reseau = parametres.reseau === true;
  }
}

/**
 * Clé i18n du message affichable.
 *
 * `errors.<CODE>` est résolu dans le namespace `ui` (ns par défaut) : la clé complète est donc
 * `ui:errors.<CODE>`, exactement la convention de DECISIONS §9.4.
 */
export function cleI18nErreur(erreur: ErreurApi): string {
  return erreur.reseau ? 'erreurs.reseau' : `erreurs.${erreur.code}`;
}

/** Normalise n’importe quelle exception en `ErreurApi` (jamais de `unknown` qui fuit). */
export function versErreurApi(inconnu: unknown): ErreurApi {
  if (inconnu instanceof ErreurApi) return inconnu;
  return new ErreurApi({
    code: 'INTERNAL_ERROR',
    message: inconnu instanceof Error ? inconnu.message : 'Erreur inconnue',
    statutHttp: 0,
    reseau: true,
  });
}
